(() => {
  'use strict';

  if (window.__MT_CART_REWARD_V11__) return;
  window.__MT_CART_REWARD_V11__ = true;

  const TRACKER_SELECTOR = '#CartDrawer [data-mt-reward-tracker]';
  const CONFIG_SELECTOR = '#CartDrawer [data-mt-reward-config]';
  const DRAWER_SELECTOR = '#CartDrawer';
  const CELEBRATION_SELECTOR = '#CartDrawer [data-mt-cart-celebration]';
  const LOTTIE_TAG = 'dotlottie-wc';
  const LOTTIE_LIBRARY_URL = 'https://unpkg.com/@lottiefiles/dotlottie-wc@0.9.4/dist/dotlottie-wc.js';
  const LOTTIE_FILE_URL = 'https://lottie.host/67146a31-d885-4272-aa99-0ba030f15a0c/sh210GHIYM.lottie';
  const DEFAULT_MUESLI_IMAGE = 'https://cdn.shopify.com/s/files/1/0755/6241/8365/files/image-Photoroom_2_2.png?v=1783752907';

  const CELEBRATION_MS = 4000;
  const MILESTONE_POSITIONS = [8, 38, 68, 96];

  const DEFAULT_SLABS = [
    {
      tier: 1,
      threshold: 39900,
      offer: 'FREE Super Muesli Choco Crunch 40g',
      value: 'Reward worth ₹50',
      images: [DEFAULT_MUESLI_IMAGE]
    },
    {
      tier: 2,
      threshold: 59900,
      offer: 'FREE Super Muesli Choco Crunch 100g',
      value: 'Reward worth ₹110',
      images: [DEFAULT_MUESLI_IMAGE]
    },
    {
      tier: 3,
      threshold: 79900,
      offer: 'FREE Shipping + FREE Multimillet Dosa Instant Mix',
      value: 'Reward worth ₹110',
      images: ['']
    },
    {
      tier: 4,
      threshold: 109900,
      offer: 'FREE Shipping + FREE Super Muesli Choco Crunch 100g + FREE Multimillet Upma Instant Mix',
      value: 'Total reward value ₹220',
      images: [DEFAULT_MUESLI_IMAGE, '']
    }
  ];

  let SLABS = DEFAULT_SLABS.map((slab) => ({ ...slab, images: [...slab.images] }));
  let rewardConfigSignature = '';
  let lastTrackerNode = null;

  const nativeFetch = window.fetch.bind(window);
  const nativeXhrOpen = XMLHttpRequest.prototype.open;
  const nativeXhrSend = XMLHttpRequest.prototype.send;

  let lottieLibraryPromise = null;
  let syncTimer = 0;
  let syncRequestId = 0;
  let lastCartTotal = null;
  let lastTier = null;
  // The slab we last fired the animation for. Only this value decides whether a
  // celebration is owed, so a silent background sync can never swallow one.
  let lastCelebratedTier = null;
  let initialCartSynced = false;
  let lastRenderTime = 0;
  let celebrationPlaying = false;
  let celebrationQueue = 0;
  let celebrationTimer = 0;
  let drawerPollTimer = 0;
  let mutationArmedUntil = 0;

  const getDrawer = () => document.querySelector(DRAWER_SELECTOR);
  const getTracker = () => document.querySelector(TRACKER_SELECTOR);
  const getCelebrationNode = () => document.querySelector(CELEBRATION_SELECTOR);

  const getConfigNode = () => document.querySelector(CONFIG_SELECTOR);

  const formatSlabLabel = (threshold) => {
    const rupees = Math.max(0, Number(threshold || 0)) / 100;
    return `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(rupees)}`;
  };

  const escapeHtml = (value = '') => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const normalizeSlabs = (input) => {
    let previousThreshold = 0;

    return DEFAULT_SLABS.map((fallback, index) => {
      const source = input?.[index] || {};
      let threshold = Number(source.threshold);

      if (!Number.isFinite(threshold) || threshold <= previousThreshold) {
        threshold = fallback.threshold > previousThreshold
          ? fallback.threshold
          : previousThreshold + 100;
      }

      previousThreshold = threshold;

      const sourceImages = Array.isArray(source.images) ? source.images : [];
      const fallbackImages = fallback.images || [];
      const imageCount = index === 3 ? 2 : 1;
      const images = Array.from({ length: imageCount }, (_, imageIndex) => {
        const sourceImage = String(sourceImages[imageIndex] || '').trim();
        return sourceImage || String(fallbackImages[imageIndex] || '').trim();
      });

      return {
        tier: index + 1,
        threshold,
        label: formatSlabLabel(threshold),
        offer: String(source.offer || fallback.offer).trim(),
        value: String(source.value || fallback.value).trim(),
        images
      };
    });
  };

  const refreshRewardConfig = () => {
    const configNode = getConfigNode();
    const raw = configNode?.textContent?.trim() || '';
    const nextSignature = raw || '__defaults__';

    if (nextSignature === rewardConfigSignature) return false;

    try {
      const parsed = raw ? JSON.parse(raw) : null;
      SLABS = normalizeSlabs(parsed?.slabs);
    } catch (error) {
      console.warn('[Millet Twist rewards] Invalid Theme Editor reward configuration. Falling back to defaults.', error);
      SLABS = normalizeSlabs(null);
    }

    rewardConfigSignature = nextSignature;
    return true;
  };

  const getCartUrl = () => {
    const root = window.Shopify?.routes?.root || '/';
    const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
    return new URL(`${normalizedRoot}cart.js`, window.location.origin).toString();
  };

  const isCartMutationUrl = (input, init = {}) => {
    try {
      const rawUrl = typeof input === 'string' ? input : input?.url;
      if (!rawUrl) return false;

      const method = String(init.method || input?.method || 'GET').toUpperCase();
      if (method === 'GET' || method === 'HEAD') return false;

      const pathname = new URL(rawUrl, window.location.origin).pathname;
      return /\/cart\/(add|change|update|clear)(\.js)?\/?$/i.test(pathname);
    } catch (_error) {
      return false;
    }
  };

  const tierFromTotal = (total) => {
    for (let index = SLABS.length - 1; index >= 0; index -= 1) {
      if (total >= SLABS[index].threshold) return index + 1;
    }
    return 0;
  };

  const progressFromTotal = (total) => {
    if (total <= 0) return 0;

    if (total < SLABS[0].threshold) {
      return Math.max(0, Math.min(MILESTONE_POSITIONS[0], (total / SLABS[0].threshold) * MILESTONE_POSITIONS[0]));
    }

    for (let index = 0; index < SLABS.length - 1; index += 1) {
      const currentThreshold = SLABS[index].threshold;
      const nextThreshold = SLABS[index + 1].threshold;

      if (total < nextThreshold) {
        const startPosition = MILESTONE_POSITIONS[index];
        const endPosition = MILESTONE_POSITIONS[index + 1];
        const span = Math.max(1, nextThreshold - currentThreshold);
        const ratio = Math.max(0, Math.min(1, (total - currentThreshold) / span));
        return startPosition + ((endPosition - startPosition) * ratio);
      }
    }

    return MILESTONE_POSITIONS[MILESTONE_POSITIONS.length - 1];
  };

  const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
    const safeNumber = Number(number);
    if (!Number.isFinite(safeNumber)) return '0';

    const fixed = safeNumber.toFixed(precision);
    const parts = fixed.split('.');
    parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    return parts[1] ? `${parts[0]}${decimal}${parts[1]}` : parts[0];
  };

  const formatMoney = (cents, tracker = getTracker()) => {
    const format = tracker?.dataset?.mtMoneyFormat || 'Rs. {{ amount }}';
    const placeholderMatch = format.match(/\{\{\s*(\w+)\s*\}\}/);
    const placeholder = placeholderMatch?.[1] || 'amount';
    const amount = Number(cents || 0) / 100;

    let formatted;
    switch (placeholder) {
      case 'amount_no_decimals':
        formatted = formatWithDelimiters(amount, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        formatted = formatWithDelimiters(amount, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = formatWithDelimiters(amount, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        formatted = formatWithDelimiters(amount, 2, "'", '.');
        break;
      default:
        formatted = formatWithDelimiters(amount, 2, ',', '.');
        break;
    }

    return format.replace(placeholderMatch?.[0] || '{{ amount }}', formatted);
  };

  const rewardImageMarkup = (src, alt, size = 220) => {
    if (!src) {
      return '<div class="mt-current-reward__placeholder">REWARD</div>';
    }

    return `
      <img
        class="mt-current-reward__image"
        src="${escapeHtml(src)}"
        alt="${escapeHtml(alt)}"
        width="${size}"
        height="${size}"
        loading="eager"
      >`;
  };

  const rewardMediaMarkup = (tier) => {
    const slab = tier === 0 ? SLABS[0] : SLABS[Math.max(0, tier - 1)];
    if (!slab) return '<div class="mt-current-reward__placeholder">REWARD</div>';

    if (tier === 4) {
      return `
        <div class="mt-current-reward__mini">
          ${rewardImageMarkup(slab.images?.[0], slab.offer, 160)}
        </div>
        <div class="mt-current-reward__mini">
          ${rewardImageMarkup(slab.images?.[1], slab.offer, 160)}
        </div>`;
    }

    return rewardImageMarkup(slab.images?.[0], slab.offer, 220);
  };

  const updateMessage = (tracker, total, tier) => {
    const icon = tracker.querySelector('.mt-reward-tracker__message-icon');
    const message = tracker.querySelector('.mt-reward-tracker__message > span:last-child');
    if (icon) icon.textContent = tier > 0 ? '✓' : '🎁';
    if (!message) return;

    if (tier === SLABS.length) {
      message.innerHTML = `<strong>Top slab reached.</strong> You qualify for the ${SLABS[SLABS.length - 1].label} reward.`;
      return;
    }

    const nextSlab = SLABS[tier];
    const remaining = Math.max(0, nextSlab.threshold - total);

    if (tier > 0) {
      message.innerHTML = `You qualify for the <strong>${SLABS[tier - 1].label}</strong> slab. Add <strong>${formatMoney(remaining, tracker)}</strong> more to upgrade to the ${nextSlab.label} reward slab.`;
    } else {
      message.innerHTML = `Add <strong>${formatMoney(remaining, tracker)}</strong> to unlock the ${SLABS[0].label} reward slab.`;
    }
  };

  const updateMilestones = (tracker, tier) => {
    const milestones = tracker.querySelectorAll('.mt-reward-progress__milestone');

    milestones.forEach((milestone, index) => {
      const slab = SLABS[index];
      if (!slab) return;

      const slabTier = index + 1;
      milestone.classList.toggle('is-passed', tier > slabTier);
      milestone.classList.toggle('is-current', tier === slabTier);
      milestone.style.setProperty('--mt-slab-position', `${MILESTONE_POSITIONS[index]}%`);

      const dot = milestone.querySelector('.mt-reward-progress__dot');
      if (dot) dot.textContent = tier >= slabTier ? '✓' : '';

      const price = milestone.querySelector('.mt-reward-progress__price');
      if (price) price.textContent = slab.label;
    });
  };

  const updateCurrentReward = (tracker, tier) => {
    const reward = tracker.querySelector('.mt-current-reward');
    if (!reward) return;

    const displaySlab = tier === 0 ? SLABS[0] : SLABS[tier - 1];
    const displayLabel = tier === 0 ? 'Your next reward' : 'Your current reward';

    reward.classList.toggle('is-next', tier === 0);
    reward.classList.toggle('is-active', tier > 0);
    reward.setAttribute('aria-label', displayLabel);

    const media = reward.querySelector('.mt-current-reward__media');
    if (media) {
      media.classList.toggle('is-double', tier === 4);
      const renderedKey = `${tier}:${rewardConfigSignature}`;
      if (media.dataset.mtRenderedTier !== renderedKey) {
        media.innerHTML = rewardMediaMarkup(tier);
        media.dataset.mtRenderedTier = renderedKey;
      }
    }

    const eyebrow = reward.querySelector('.mt-current-reward__eyebrow');
    const offer = reward.querySelector('h4');
    const value = reward.querySelector('p');

    if (eyebrow) eyebrow.textContent = `${displayLabel} · ${displaySlab.label} slab`;
    if (offer) offer.textContent = displaySlab.offer;
    if (value) value.textContent = displaySlab.value;
  };

  const updateCards = (tracker, tier) => {
    const cards = tracker.querySelectorAll('.mt-reward-card');

    cards.forEach((card, index) => {
      const slab = SLABS[index];
      if (!slab) return;

      const slabTier = index + 1;
      const isCurrent = tier === slabTier;
      const isPassed = tier > slabTier;
      const isNext = slabTier === tier + 1;

      card.classList.toggle('is-current', isCurrent);
      card.classList.toggle('is-passed', isPassed);
      card.classList.toggle('is-next', isNext);

      const slabLabel = card.querySelector('.mt-reward-card__meta strong');
      if (slabLabel) slabLabel.textContent = slab.label;

      const offer = card.querySelector('h4');
      if (offer) offer.textContent = slab.offer;

      const value = card.querySelector('p');
      if (value) value.textContent = slab.value;

      const status = card.querySelector('.mt-reward-card__meta span');
      if (status) {
        status.textContent = isCurrent
          ? 'Your reward'
          : isPassed
            ? 'Previous slab'
            : isNext
              ? 'Next slab'
              : 'Locked';
      }
    });
  };

  const renderTracker = (total) => {
    const tracker = getTracker();
    if (!tracker) return null;

    const trackerChanged = tracker !== lastTrackerNode;
    lastTrackerNode = tracker;

    const configChanged = refreshRewardConfig();
    const tier = tierFromTotal(total);
    const progress = progressFromTotal(total);

    // Avoid re-writing the same DOM repeatedly, unless the section/config was replaced.
    if (!configChanged && !trackerChanged && tracker.dataset.mtLiveTotal === String(total) && tracker.dataset.mtCurrentTier === String(tier)) {
      return tier;
    }

    tracker.dataset.mtLiveTotal = String(total);
    tracker.dataset.mtCurrentTier = String(tier);
    tracker.style.setProperty('--mt-reward-progress', `${progress}%`);

    const totalNode = tracker.querySelector('.mt-reward-tracker__cart-total strong');
    if (totalNode) totalNode.innerHTML = formatMoney(total, tracker);

    const progressNode = tracker.querySelector('.mt-reward-progress');
    if (progressNode) {
      const percentage = Math.max(0, Math.min(100, Math.round((total * 100) / SLABS[3].threshold)));
      progressNode.setAttribute('aria-valuenow', String(percentage));
      progressNode.setAttribute('aria-valuetext', `${formatMoney(total, tracker)} out of ${formatMoney(SLABS[3].threshold, tracker)}`);
    }

    updateMessage(tracker, total, tier);
    updateMilestones(tracker, tier);
    updateCurrentReward(tracker, tier);
    updateCards(tracker, tier);

    lastRenderTime = Date.now();
    return tier;
  };

  const isDrawerVisible = (drawer = getDrawer()) => {
    if (!(drawer instanceof HTMLElement) || drawer.hidden) return false;

    const rect = drawer.getBoundingClientRect();
    const style = window.getComputedStyle(drawer);

    return (
      rect.width > 40 &&
      rect.height > 100 &&
      rect.right > 0 &&
      rect.left < window.innerWidth &&
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      Number(style.opacity || 1) > 0
    );
  };

  /* ------------------------------------------------------------------------
     Lottie library
     ------------------------------------------------------------------------ */

  const ensureLottieLibrary = () => {
    if (window.customElements?.get(LOTTIE_TAG)) return Promise.resolve(true);
    if (lottieLibraryPromise) return lottieLibraryPromise;

    lottieLibraryPromise = new Promise((resolve) => {
      let settled = false;
      const finish = (value) => {
        if (settled) return;
        settled = true;
        if (!value) {
          console.warn('[Millet Twist rewards] Lottie library unavailable; using the CSS fallback.');
        }
        resolve(value);
      };

      const waitForDefinition = () => {
        if (window.customElements?.get(LOTTIE_TAG)) {
          finish(true);
          return;
        }

        if (!window.customElements?.whenDefined) {
          finish(false);
          return;
        }

        Promise.race([
          window.customElements.whenDefined(LOTTIE_TAG).then(() => true),
          new Promise((done) => window.setTimeout(() => done(false), 8000))
        ]).then(finish);
      };

      const existing = [...document.scripts].find((script) =>
        script.src.includes('@lottiefiles/dotlottie-wc')
      );

      if (existing) {
        waitForDefinition();
        return;
      }

      const script = document.createElement('script');
      script.type = 'module';
      script.src = LOTTIE_LIBRARY_URL;
      script.dataset.mtLottieLibrary = 'true';
      script.addEventListener('load', waitForDefinition, { once: true });
      script.addEventListener('error', () => finish(false), { once: true });
      document.head.appendChild(script);
    });

    return lottieLibraryPromise;
  };

  /* ------------------------------------------------------------------------
     Celebration
     ------------------------------------------------------------------------
     The container lives permanently in the drawer markup, so there is no
     overlay to build, position, or clean up. Players are injected fresh on
     each run, which restarts the animation reliably without depending on the
     player's own playback API.
     ------------------------------------------------------------------------ */

  const clearPlayers = (node = getCelebrationNode()) => {
    node?.querySelectorAll('.mt-cart-lottie__player').forEach((slot) => {
      slot.replaceChildren();
    });
  };

  const injectPlayers = (node) => {
    const slots = node.querySelectorAll('.mt-cart-lottie__player');

    if (!slots.length) {
      console.warn('[Millet Twist rewards] Celebration slots missing from the drawer markup.');
      return false;
    }

    slots.forEach((slot) => {
      const player = document.createElement(LOTTIE_TAG);
      player.setAttribute('src', LOTTIE_FILE_URL);
      player.setAttribute('autoplay', '');
      player.setAttribute('loop', '');
      slot.replaceChildren(player);
    });

    return true;
  };

  const finishCelebration = () => {
    window.clearTimeout(celebrationTimer);

    const node = getCelebrationNode();
    if (node) node.classList.remove('is-visible');

    window.setTimeout(() => {
      clearPlayers();
      celebrationPlaying = false;
      if (celebrationQueue > 0) window.setTimeout(playCelebration, 140);
    }, 300);
  };

  const playCelebration = async () => {
    if (celebrationPlaying || celebrationQueue < 1) return;
    if (!isDrawerVisible()) return;

    const node = getCelebrationNode();
    if (!node) {
      console.warn('[Millet Twist rewards] Celebration container not found inside #CartDrawer.');
      return;
    }

    celebrationQueue -= 1;
    celebrationPlaying = true;

    node.hidden = false;
    await ensureLottieLibrary();

    // The drawer may have closed while the library was loading.
    if (!isDrawerVisible()) {
      celebrationPlaying = false;
      celebrationQueue += 1;
      return;
    }

    // Reveal the container BEFORE the players are created, so the canvas is
    // never measured inside a zero-opacity or hidden box.
    node.classList.add('is-visible');
    void node.offsetWidth;

    injectPlayers(node);

    window.requestAnimationFrame(() => {
      node.querySelectorAll(LOTTIE_TAG).forEach((player) => {
        try {
          if (typeof player.play === 'function') player.play();
        } catch (_error) {}
      });
    });

    window.clearTimeout(celebrationTimer);
    celebrationTimer = window.setTimeout(finishCelebration, CELEBRATION_MS);
  };

  const queueCelebration = (count = 1) => {
    celebrationQueue = Math.min(8, celebrationQueue + Math.max(1, count));
    playCelebration();
  };

  /* ------------------------------------------------------------------------
     Cart state
     ------------------------------------------------------------------------ */

  const applyCartState = (total) => {
    const safeTotal = Math.max(0, Number(total || 0));
    const newTier = tierFromTotal(safeTotal);

    renderTracker(safeTotal);

    // First read of the session establishes the baseline. A page load or a
    // drawer re-open must never fire the animation on its own.
    if (!initialCartSynced) {
      lastCartTotal = safeTotal;
      lastTier = newTier;
      lastCelebratedTier = newTier;
      initialCartSynced = true;
      return;
    }

    lastCartTotal = safeTotal;
    lastTier = newTier;

    if (lastCelebratedTier === null) {
      lastCelebratedTier = newTier;
      return;
    }

    if (newTier > lastCelebratedTier) {
      // A single add can jump more than one slab, e.g. 0 straight to the 799 slab.
      const jumps = newTier - lastCelebratedTier;
      lastCelebratedTier = newTier;
      queueCelebration(jumps);
      return;
    }

    if (newTier < lastCelebratedTier) {
      // Dropped below a slab. Re-arm so climbing back up plays again.
      lastCelebratedTier = newTier;
    }
  };

  const syncCart = async () => {
    const requestId = ++syncRequestId;

    try {
      const cartUrl = new URL(getCartUrl());
      cartUrl.searchParams.set('_mt_reward', String(Date.now()));

      const response = await nativeFetch(cartUrl.toString(), {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
      const cart = await response.json();
      if (requestId !== syncRequestId) return;

      applyCartState(Number(cart.total_price || 0));
    } catch (error) {
      console.warn('[Millet Twist rewards] Unable to refresh cart value.', error);
    }
  };

  const requestSync = (delay = 50) => {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(syncCart, delay);
  };

  const armCartMutation = () => {
    mutationArmedUntil = Date.now() + 2600;

    // Shopify themes differ in when /cart.js reflects the final state. These
    // short retries remove the visible cart-value lag without permanent rapid polling.
    [0, 100, 260, 650, 1200].forEach((delay) => {
      window.setTimeout(() => requestSync(0), delay);
    });
  };

  const patchCartRequests = () => {
    window.fetch = async (...args) => {
      const response = await nativeFetch(...args);
      if (isCartMutationUrl(args[0], args[1] || {})) armCartMutation();
      return response;
    };

    XMLHttpRequest.prototype.open = function patchedOpen(method, url, ...rest) {
      this.__mtCartMutation = isCartMutationUrl(url, { method });
      return nativeXhrOpen.call(this, method, url, ...rest);
    };

    XMLHttpRequest.prototype.send = function patchedSend(...args) {
      if (this.__mtCartMutation) {
        this.addEventListener('loadend', armCartMutation, { once: true });
      }
      return nativeXhrSend.apply(this, args);
    };
  };

  const startObserver = () => {
    const observer = new MutationObserver((mutations) => {
      if (Date.now() - lastRenderTime < 140) return;

      const relevant = mutations.some((mutation) => {
        // Ignore our own celebration DOM writes, or the observer chases its own
        // tail every time the players are injected or cleared.
        const target = mutation.target;
        if (target instanceof Element && target.closest?.(CELEBRATION_SELECTOR)) return false;

        return (
          mutation.type === 'childList' ||
          (mutation.type === 'attributes' && ['class', 'hidden', 'open', 'aria-hidden'].includes(mutation.attributeName))
        );
      });

      if (!relevant) return;
      requestSync(80);

      if (celebrationQueue > 0 && !celebrationPlaying && isDrawerVisible()) {
        playCelebration();
      }
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'hidden', 'open', 'aria-hidden']
    });
  };

  const startInteractionFallback = () => {
    document.addEventListener('click', (event) => {
      const drawer = event.target.closest?.(DRAWER_SELECTOR);
      if (!drawer) return;

      const control = event.target.closest?.('button, a, input');
      if (!control) return;
      if (control.matches('[name="checkout"], [href*="/checkout"]')) return;

      const signature = `${control.className || ''} ${control.getAttribute('name') || ''} ${control.getAttribute('aria-label') || ''}`;
      if (/quantity|remove|delete|minus|plus|add|cart/i.test(signature)) {
        window.setTimeout(armCartMutation, 40);
      }
    }, true);

    document.addEventListener('change', (event) => {
      if (event.target.closest?.(DRAWER_SELECTOR)) armCartMutation();
    }, true);
  };

  const startDrawerPolling = () => {
    window.clearInterval(drawerPollTimer);
    drawerPollTimer = window.setInterval(() => {
      if (!isDrawerVisible()) return;
      requestSync(0);
      if (celebrationQueue > 0 && !celebrationPlaying) playCelebration();
    }, 900);
  };

  const primeBaselineFromMarkup = () => {
    refreshRewardConfig();
    const tracker = getTracker();
    const markupTotal = Number(tracker?.dataset?.mtLiveTotal);
    if (!Number.isFinite(markupTotal)) return;

    lastCartTotal = markupTotal;
    lastTier = tierFromTotal(markupTotal);
    lastCelebratedTier = lastTier;
    initialCartSynced = true;
  };

  const start = () => {
    primeBaselineFromMarkup();
    patchCartRequests();
    startObserver();
    startInteractionFallback();
    startDrawerPolling();
    ensureLottieLibrary();
    requestSync(0);

    [
      'cart:updated',
      'cart:refresh',
      'cart:change',
      'cart:build',
      'ajaxProduct:added'
    ].forEach((eventName) => {
      document.addEventListener(eventName, armCartMutation);
      window.addEventListener(eventName, armCartMutation);
    });

    ['shopify:section:load', 'shopify:section:reorder'].forEach((eventName) => {
      document.addEventListener(eventName, () => requestSync(0));
    });

    // Console helpers:
    //   MilletTwistRewards.playCelebration()  → force the animation
    //   MilletTwistRewards.diagnose()         → check every prerequisite at once
    window.MilletTwistRewards = {
      playCelebration: () => queueCelebration(1),
      refresh: () => syncCart(),
      state: () => ({
        lastCartTotal,
        lastTier,
        lastCelebratedTier,
        celebrationQueue,
        celebrationPlaying,
        drawerVisible: isDrawerVisible(),
        mutationArmed: Date.now() < mutationArmedUntil,
        slabs: SLABS.map((slab) => ({ ...slab, images: [...slab.images] }))
      }),
      diagnose: async () => {
        const node = getCelebrationNode();
        const styles = node ? window.getComputedStyle(node) : null;

        const report = {
          trackerFound: Boolean(getTracker()),
          drawerFound: Boolean(getDrawer()),
          drawerVisible: isDrawerVisible(),
          celebrationNodeFound: Boolean(node),
          celebrationSlots: node ? node.querySelectorAll('.mt-cart-lottie__player').length : 0,
          celebrationDisplay: styles ? styles.display : null,
          celebrationPosition: styles ? styles.position : null,
          libraryDefined: Boolean(window.customElements?.get(LOTTIE_TAG))
        };

        try {
          const response = await nativeFetch(LOTTIE_FILE_URL, { method: 'GET', mode: 'cors' });
          report.lottieFileStatus = response.status;
        } catch (error) {
          report.lottieFileStatus = `blocked: ${error.message}`;
        }

        console.table(report);
        return report;
      }
    };
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();